import api from '../../../api/client';
import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Drawer,
  Modal,
  Form,
  Button,
  Select,
  Input,
  InputNumber,
  Space,
  message,
  Card,
  Row,
  Col,
  Typography,
  Descriptions,
  Popconfirm,
  DatePicker,
  Divider,
  Statistic,
  Tag,
  Tooltip,
  Alert,
  Timeline
} from "antd";
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  SearchOutlined,
  ReloadOutlined,
  RollbackOutlined,
  HistoryOutlined,
  WarningOutlined
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import config from "../../../config";
import { usePermissions } from "../../../permissions";
import { useSelector } from "react-redux";
import { groupAccountOptions } from "../../../utils/account";
import { formatKES } from "../../../utils/currency";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ManualJournalList = () => {
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [glAccounts, setGlAccounts] = useState([]);
  const [paidAccounts, setPaidAccounts] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedJournal, setSelectedJournal] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEditId, setCurrentEditId] = useState(null);
  const [reversalModalVisible, setReversalModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [reversalLoading, setReversalLoading] = useState(false);
  const [reversalHistory, setReversalHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [reversalForm] = Form.useForm();
  const [form] = Form.useForm();

  const { createPermission, editPermission } = usePermissions();
  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;
  const account = useSelector((state) => state.account);
  const userId = account?.user?.id;
  const userName = account?.user?.firstname;

  // Fetch chart of accounts for dropdowns
  const fetchGlAccounts = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/chart-of-accounts`);
      const options = data.map((acc) => ({
        value: acc.gl_code,
        label: `${acc.gl_code} - ${acc.account_name}`,
      }));
      setGlAccounts(options);
    } catch (error) {
      console.error("Failed to fetch GL accounts", error);
    }
  }, [apiUrl]);

  // Fetch paid-through accounts for the form
  const fetchPaidAccounts = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/paid-through`);
      setPaidAccounts(data);
    } catch (error) {
      console.error("Failed to fetch paid-through accounts", error);
    }
  }, [apiUrl]);

  // Fetch manual journal entries
  const fetchJournals = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/api/transactions/manual-entries`,
      );
      setJournals(data);
    } catch (error) {
      message.error("Failed to fetch journal entries");
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  // Fetch reversal history for an entry
  const fetchReversalHistory = async (id) => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get(
        `/transactions/manual-entries/${id}/reversal-history`
      );
      if (data.success) {
        setReversalHistory(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch reversal history", error);
      message.error("Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchGlAccounts();
    fetchPaidAccounts();
    fetchJournals();
  }, [fetchGlAccounts, fetchPaidAccounts, fetchJournals]);

  // Client-side search
  const filteredJournals = journals.filter((item) => {
    if (!searchText) return true;
    const term = searchText.toLowerCase();
    return (
      item.description?.toLowerCase().includes(term) ||
      item.transaction_code?.toLowerCase().includes(term) ||
      item.amount?.toString().includes(term) ||
      (glAccounts.find((o) => o.value === item.gl_debit)?.label || "")
        .toLowerCase()
        .includes(term) ||
      (glAccounts.find((o) => o.value === item.gl_credit)?.label || "")
        .toLowerCase()
        .includes(term)
    );
  });

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      "ID",
      "Date",
      "Amount (KES)",
      "Debit Account",
      "Credit Account",
      "Description",
      "Transaction Code",
      "Status",
      "Reversal Reason"
    ];
    const rows = filteredJournals.map((j) => [
      j.id,
      dayjs(j.transaction_date).format("YYYY-MM-DD"),
      j.amount.toLocaleString(),
      glAccounts.find((o) => o.value === j.gl_debit)?.label || j.gl_debit,
      glAccounts.find((o) => o.value === j.gl_credit)?.label || j.gl_credit,
      j.description,
      j.transaction_code,
      j.reversed ? "Reversed" : "Active",
      j.reversal_reason || "-"
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map(cell => `"${cell}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `manual_journals_${dayjs().format("YYYYMMDD_HHmmss")}.csv`);
  };

  // View details in drawer
  const handleView = (record) => {
    setSelectedJournal(record);
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setSelectedJournal(null);
  };

  // Open create modal
  const handleCreate = () => {
    setEditMode(false);
    setCurrentEditId(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Open edit modal (only for non-reversed entries)
  const handleEdit = async (id) => {
    const entry = journals.find(j => j.id === id);
    if (entry?.reversed) {
      message.warning("Cannot edit a reversed entry");
      return;
    }
    try {
      const { data } = await api.get(
        `/transactions/manual-entries/${id}`,
      );
      setEditMode(true);
      setCurrentEditId(id);
      form.setFieldsValue({
        transaction_date: dayjs(data.transaction_date),
        amount: data.amount,
        gl_debit: data.gl_debit,
        gl_credit: data.gl_credit,
        description: data.description,
      });
      setModalVisible(true);
    } catch (error) {
      message.error("Failed to load entry for editing");
    }
  };

  // Reverse entry (instead of delete)
  const handleReverse = async (id) => {
    const entry = journals.find(j => j.id === id);
    if (entry?.reversed) {
      message.warning("This entry has already been reversed");
      return;
    }
    setSelectedJournal(entry);
    reversalForm.resetFields();
    setReversalModalVisible(true);
  };

  // Submit reversal
  const handleSubmitReversal = async () => {
    try {
      const values = await reversalForm.validateFields();
      setReversalLoading(true);
      
      const { data } = await api.post(
        `/transactions/manual-entries/${selectedJournal.id}/reverse`,
        {
          reason: values.reason,
          reason_detail: values.reason_detail,
          remarks: values.remarks,
          reversed_by: userId,
          reversed_by_name: userName
        }
      );

      if (data.success) {
        message.success(data.message);
        setReversalModalVisible(false);
        reversalForm.resetFields();
        fetchJournals();
      } else {
        message.error(data.message || "Reversal failed");
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to reverse entry");
    } finally {
      setReversalLoading(false);
    }
  };

  // View reversal history
  const handleViewHistory = async (record) => {
    setSelectedJournal(record);
    await fetchReversalHistory(record.id);
    setHistoryModalVisible(true);
  };

  // Submit create/edit form
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        transaction_date: values.transaction_date.format("YYYY-MM-DD"),
        amount: values.amount,
        gl_debit: values.gl_debit,
        gl_credit: values.gl_credit,
        description: values.description,
        userName,
        userId,
      };

      let response;
      if (editMode && currentEditId) {
        response = await api.put(
          `/transactions/manual-entries/${currentEditId}`,
          payload,
        );
      } else {
        response = await api.post(
          `/api/transactions/manual-entry`,
          payload,
        );
      }

      if (response.data.message || response.data.success) {
        message.success(response.data.message || "Entry saved");
        setModalVisible(false);
        fetchJournals();
        form.resetFields();
      } else {
        message.error("Operation failed");
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Validation failed");
    }
  };

  const paidOptions = groupAccountOptions(paidAccounts);

  // Table columns with reversal action
  const columns = [
    {
      title: "Date",
      dataIndex: "transaction_date",
      key: "date",
      render: (date) => dayjs(date).format("YYYY-MM-DD"),
      sorter: (a, b) =>
        dayjs(a.transaction_date).unix() - dayjs(b.transaction_date).unix(),
    },
    {
      title: "TRN Code",
      dataIndex: "transaction_code",
      key: "transaction_code",
      ellipsis: true,
      render: (text, record) => (
        <Space>
          <Text>{text}</Text>
          {record.is_reversal && <Tag color="orange">Reversal</Tag>}
          {record.reversed && !record.is_reversal && <Tag color="red">Reversed</Tag>}
        </Space>
      ),
    },
    {
      title: "Amount (KES)",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => formatKES(amount),
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: "Debit Account",
      dataIndex: "gl_debit",
      key: "debit",
      render: (code) => glAccounts.find((o) => o.value === code)?.label || code,
    },
    {
      title: "Credit Account",
      dataIndex: "gl_credit",
      key: "credit",
      render: (code) => glAccounts.find((o) => o.value === code)?.label || code,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          <Tooltip title="View History">
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => handleViewHistory(record)}
            />
          </Tooltip>
          
          {!record.reversed && !record.is_reversal && editPermission && (
            <Tooltip title="Edit Entry">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record.id)}
              />
            </Tooltip>
          )}
          
          {!record.reversed && !record.is_reversal && (
            <Tooltip title="Reverse Entry (Creates Offset)">
              <Button
                size="small"
                danger
                icon={<RollbackOutlined />}
                onClick={() => handleReverse(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const totalCounts = filteredJournals.length;
  const totalAmount = filteredJournals.reduce((sum, p) => sum + p.amount, 0);
  const reversedCount = filteredJournals.filter(j => j.reversed === 1).length;

  return (
    <>
      <Card
        title="Manual Journal Entries"
        extra={
          <Space>
            <Input.Search
              placeholder="Search by description, account, amount..."
              allowClear
              style={{ width: 280 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={(val) => setSearchText(val)}
            />
            <Button icon={<DownloadOutlined />} onClick={exportToCSV}>
              Export CSV
            </Button>
            {createPermission && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Manual Entry
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={fetchJournals}>
              Refresh
            </Button>
          </Space>
        }
      >
        <Divider />

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Statistic title="Total Entries" value={totalCounts} />
          </Col>
          <Col span={8}>
            <Statistic
              title="Total Amount (KES)"
              value={formatKES(totalAmount)}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Reversed Entries"
              value={reversedCount}
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
        </Row>
        <Divider />
        <Table
          size="small"
          width="100%"
          columns={columns}
          dataSource={filteredJournals}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          scroll={{ x: "max-content" }}
        />
      </Card>

      {/* Details Drawer */}
      <Drawer
        title="Journal Entry Details"
        placement="right"
        width={600}
        onClose={closeDrawer}
        open={drawerVisible}
      >
        {selectedJournal && (
          <>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="ID">
                {selectedJournal.id}
              </Descriptions.Item>
              <Descriptions.Item label="Transaction Code">
                <Space>
                  {selectedJournal.transaction_code}
                  {selectedJournal.is_reversal && <Tag color="orange">Reversal Entry</Tag>}
                  {selectedJournal.reversed && !selectedJournal.is_reversal && <Tag color="red">Reversed</Tag>}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Date">
                {dayjs(selectedJournal.transaction_date).format("YYYY-MM-DD HH:mm")}
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                {formatKES(selectedJournal.amount)}
              </Descriptions.Item>
              <Descriptions.Item label="Debit Account">
                {glAccounts.find((o) => o.value === selectedJournal.gl_debit)?.label || selectedJournal.gl_debit}
              </Descriptions.Item>
              <Descriptions.Item label="Credit Account">
                {glAccounts.find((o) => o.value === selectedJournal.gl_credit)?.label || selectedJournal.gl_credit}
              </Descriptions.Item>
              <Descriptions.Item label="Description">
                {selectedJournal.description}
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {dayjs(selectedJournal.created_at).format("YYYY-MM-DD HH:mm")}
              </Descriptions.Item>
              {selectedJournal.reversed && (
                <>
                  <Descriptions.Item label="Reversed At">
                    {dayjs(selectedJournal.reversed_at).format("YYYY-MM-DD HH:mm")}
                  </Descriptions.Item>
                  <Descriptions.Item label="Reversal Reason">
                    {selectedJournal.reversal_reason}
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>
          </>
        )}
      </Drawer>

      {/* Create/Edit Modal */}
      <Modal
        title={editMode ? "Edit Journal Entry" : "Add Journal Entry"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleSubmit}>
            {editMode ? "Save Changes" : "Submit"}
          </Button>,
        ]}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="transaction_date"
                label="Journal Date"
                rules={[{ required: true, message: "Please select date" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="amount"
                label="Amount"
                rules={[{ required: true, message: "Please enter amount" }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: "100%" }}
                  placeholder="Enter amount"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="gl_debit"
                label="Debit Account"
                rules={[{ required: true, message: "Please select debit account" }]}
              >
                <Select
                  options={paidOptions}
                  placeholder="Select debit account"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="gl_credit"
                label="Credit Account"
                rules={[{ required: true, message: "Please select credit account" }]}
              >
                <Select
                  options={paidOptions}
                  placeholder="Select credit account"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={4} placeholder="Enter description (optional)" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Reversal Modal */}
      <Modal
        title={
          <Space>
            <RollbackOutlined style={{ color: "#ff4d4f" }} />
            <span>Reverse Journal Entry</span>
          </Space>
        }
        open={reversalModalVisible}
        onCancel={() => {
          setReversalModalVisible(false);
          reversalForm.resetFields();
        }}
        width={550}
        footer={[
          <Button key="cancel" onClick={() => setReversalModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="reverse"
            type="primary"
            danger
            loading={reversalLoading}
            onClick={handleSubmitReversal}
          >
            Confirm Reversal
          </Button>,
        ]}
      >
        <Alert
          message="Warning: This action cannot be undone"
          description="This will create a reversal entry that offsets the original transaction for audit purposes."
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
        />

        {selectedJournal && (
          <Descriptions size="small" bordered column={1} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Transaction Code">{selectedJournal.transaction_code}</Descriptions.Item>
            <Descriptions.Item label="Date">{dayjs(selectedJournal.transaction_date).format("YYYY-MM-DD")}</Descriptions.Item>
            <Descriptions.Item label="Amount">{formatKES(selectedJournal.amount)}</Descriptions.Item>
            <Descriptions.Item label="Debit Account">
              {glAccounts.find((o) => o.value === selectedJournal.gl_debit)?.label || selectedJournal.gl_debit}
            </Descriptions.Item>
            <Descriptions.Item label="Credit Account">
              {glAccounts.find((o) => o.value === selectedJournal.gl_credit)?.label || selectedJournal.gl_credit}
            </Descriptions.Item>
            <Descriptions.Item label="Description">{selectedJournal.description}</Descriptions.Item>
          </Descriptions>
        )}

        <Form form={reversalForm} layout="vertical">
          <Form.Item
            name="reason"
            label="Reversal Reason"
            rules={[{ required: true, message: "Please select a reason" }]}
          >
            <Select placeholder="Select a reason">
              <Option value="wrong_entry">Wrong entry / Data entry error</Option>
              <Option value="duplicate">Duplicate entry</Option>
              <Option value="cancelled">Transaction cancelled</Option>
              <Option value="adjustment">Adjustment needed</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>

          <Form.Item name="reason_detail" label="Additional Details">
            <TextArea rows={3} placeholder="Provide more details..." />
          </Form.Item>

          <Form.Item name="remarks" label="Remarks">
            <TextArea rows={2} placeholder="Any additional notes..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* History Modal */}
      <Modal
        title={
          <Space>
            <HistoryOutlined />
            <span>Transaction History - {selectedJournal?.transaction_code}</span>
          </Space>
        }
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        width={900}
        footer={null}
      >
        <Table
          columns={[
            { 
              title: "Code", 
              dataIndex: "transaction_code", 
              key: "code",
              render: (text, record) => (
                <Tag color={record.is_reversal ? "orange" : "blue"}>{text}</Tag>
              )
            },
            { title: "Date", dataIndex: "transaction_date", key: "date", render: (d) => dayjs(d).format("YYYY-MM-DD") },
            { title: "Debit", dataIndex: "gl_debit", key: "debit", render: (code) => glAccounts.find(o => o.value === code)?.label || code },
            { title: "Credit", dataIndex: "gl_credit", key: "credit", render: (code) => glAccounts.find(o => o.value === code)?.label || code },
            { title: "Amount", dataIndex: "amount", key: "amount", render: (a) => formatKES(a) },
            { 
              title: "Status", 
              key: "status",
              render: (_, record) => (
                record.is_reversal ? 
                  <Tag color="orange">Reversal Entry</Tag> : 
                  record.reversed ? 
                    <Tag color="red">Reversed</Tag> : 
                    <Tag color="green">Active</Tag>
              )
            },
            { 
              title: "Reversal Reason", 
              dataIndex: "reversal_reason", 
              key: "reason",
              render: (text) => text || "-"
            }
          ]}
          dataSource={reversalHistory}
          loading={historyLoading}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Modal>
    </>
  );
};

export default ManualJournalList;